import { useMemo, useState, useCallback, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Chip,
  Spinner,
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Divider,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useGetDatamodelsQuery, type DatamodelSchema } from '@circuitry-ai/doc-data';
import {
  CardListViewMode,
  CtyButton,
  CustomTabs,
  EntityListView,
  ListActionButtonsCell,
  formatKeyForDisplay,
  getActionsColumnDef,
  getDescriptionColumnDef,
  getNameColumnDef,
} from '@circuitry-ai/doc-ui';
import { getAiDataAccessUrl, useIsDataAccessAdmin } from './data/aiAccess/api';
import { CatalogTab } from './data/aiAccess/CatalogTab';
import { AccessMatrixTab } from './data/aiAccess/AccessMatrixTab';
import { CheckAccessTab } from './data/aiAccess/CheckAccessTab';
import { ActivityTab } from './data/aiAccess/ActivityTab';

const SCHEMA_DESCRIPTIONS: Record<string, string> = {
  claims:
    'The primary transaction unit in warranty operations — capturing fault, repair, cost, and coverage data required to validate, score, and adjudicate a reimbursement request.',
};

/** Description shown on the Data Models card; reused by the list view. */
function getSchemaDescription(schema: DatamodelSchema): string {
  return SCHEMA_DESCRIPTIONS[schema.name] ?? '';
}

const renderDataActionsCell = (onOpenDetail: (schema: DatamodelSchema) => void) =>
  function DataActionsRenderer(params: any) {
    const schema: DatamodelSchema = params?.data?.raw;
    return <DataActionsCell schema={schema} onOpenDetail={onOpenDetail} />;
  };

const DataActionsCell = ({ schema, onOpenDetail }: { readonly schema: DatamodelSchema; readonly onOpenDetail: (schema: DatamodelSchema) => void }) => {
  const { t } = useTranslation();
  return (
    <ListActionButtonsCell
      actions={[
        {
          label: t('Metrics'),
          onPress: () => onOpenDetail(schema),
          variant: 'solid',
          color: 'primary',
        },
      ]}
    />
  );
};

const DATA_TABS = {
  DATA_MODELS: 'data-models',
  CATALOG: 'catalog',
  AI_ACCESS: 'ai-access',
  CHECK_ACCESS: 'check-access',
  ACTIVITY: 'activity',
} as const;
type DataTab = typeof DATA_TABS[keyof typeof DATA_TABS];
const DATA_TAB_KEYS = Object.values(DATA_TABS) as string[];

/**
 * Sources → Data (/data). Everyone sees the Data Models list. Tenant admins also get the AI data
 * access tabs (Catalog, AI Access, Check access, Activity) where CTY_AI_DATA_ACCESS_URL is set and
 * cty-ai-data-access says they administer their tenant; see data/aiAccess/README.md. The tab is
 * kept in ?tab=; a tab mounts when first opened and then stays, so what it loaded survives switching.
 */
export default function DataPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const baseUrl = getAiDataAccessUrl();
  const admin = useIsDataAccessAdmin(baseUrl);
  const requested = searchParams.get('tab') ?? '';
  const tab = (admin && DATA_TAB_KEYS.includes(requested) ? requested : DATA_TABS.DATA_MODELS) as DataTab;

  const [opened, setOpened] = useState<Set<DataTab>>(() => new Set([tab]));
  useEffect(() => {
    setOpened((prev) => (prev.has(tab) ? prev : new Set(prev).add(tab)));
  }, [tab]);

  const tabs = [
    { key: DATA_TABS.DATA_MODELS, id: 'data-tab-data-models', title: t('Data Models') },
    { key: DATA_TABS.CATALOG, id: 'data-tab-catalog', title: t('Catalog') },
    { key: DATA_TABS.AI_ACCESS, id: 'data-tab-ai-access', title: t('AI Access') },
    { key: DATA_TABS.CHECK_ACCESS, id: 'data-tab-check-access', title: t('Check access') },
    { key: DATA_TABS.ACTIVITY, id: 'data-tab-activity', title: t('Activity') },
  ];

  // Each slot keeps its place whether or not the tabs show, so Data Models is not remounted when
  // they appear.
  const panel = (key: DataTab, content: ReactNode) => {
    if (key !== DATA_TABS.DATA_MODELS && !admin) return null;
    if (!opened.has(key) && key !== tab) return null;
    if (key !== tab) return <div className="hidden">{content}</div>;
    const spacing = key === DATA_TABS.DATA_MODELS ? '' : ' overflow-y-auto pt-4';
    return <div className={`min-h-0 flex-1${spacing}`}>{content}</div>;
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {admin && (
        <div className="shrink-0 px-6 pt-4">
          <CustomTabs
            tabs={tabs}
            selectedKey={tab}
            onTabChange={(key) => setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('tab', key);
              return next;
            })}
          />
        </div>
      )}
      {panel(DATA_TABS.DATA_MODELS, <DataModelsTab />)}
      {panel(DATA_TABS.CATALOG, <CatalogTab baseUrl={baseUrl} />)}
      {panel(DATA_TABS.AI_ACCESS, <AccessMatrixTab baseUrl={baseUrl} />)}
      {panel(DATA_TABS.CHECK_ACCESS, <CheckAccessTab baseUrl={baseUrl} />)}
      {panel(DATA_TABS.ACTIVITY, <ActivityTab baseUrl={baseUrl} />)}
    </div>
  );
}

/** The Data Models list: what /data showed before the AI data access tabs, unchanged. */
function DataModelsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewMode, setViewMode] = useState<CardListViewMode>(CardListViewMode.GRID);

  const { data, isLoading, isError, refetch } = useGetDatamodelsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const schemas = data?.data?.schemas ?? [];
  const filteredSchemas = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list = q
      ? schemas.filter(
          (s) =>
            s.name.toLowerCase().includes(q) ||
            s.id.toLowerCase().includes(q) ||
            s.metadata?.schema_name?.toLowerCase().includes(q) ||
            s.metadata?.lakehouse_name?.toLowerCase().includes(q)
        )
      : schemas;

    return [...list].sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
    );
  }, [schemas, searchQuery]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const openMetrics = useCallback(
    (schema: DatamodelSchema) => {
      const category = encodeURIComponent(schema.name ?? schema.id);
      navigate(`/data/metrics/${schema.id}?category=${category}`);
    },
    [navigate]
  );

  const listRowMapper = useCallback((schema: DatamodelSchema) => ({
    id: schema.id,
    name: formatKeyForDisplay(schema.name),
    description: getSchemaDescription(schema),
    raw: schema,
  }), []);

  const columnDefs = useMemo(
    () => [
      getNameColumnDef(t('Name')),
      getDescriptionColumnDef(t('Description')),
      getActionsColumnDef({
        headerName: t('Actions'),
        minWidth: 140,
        maxWidth: 180,
        cellRenderer: renderDataActionsCell(openMetrics),
      }),
    ],
    [t, openMetrics]
  );

  const isEmpty = filteredSchemas.length === 0;
  const showLoading = isLoading;
  const showError = isError;
  const showEmpty = !showLoading && !showError && isEmpty;

  if (showError) {
    return (
      <div className="flex flex-col h-full overflow-y-auto">
        <DataPageHeader t={t} />
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-danger">
          <Icon icon="material-symbols:error-outline" width={48} />
          <p>{t('Failed to load schemas. Try again.')}</p>
          <Button color="primary" variant="flat" onPress={handleRefresh}>
            {t('Retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (showLoading) {
    return (
      <div className="flex flex-col h-full overflow-y-auto">
        <DataPageHeader t={t} />
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (showEmpty) {
    return (
      <div className="flex flex-col h-full overflow-y-auto">
        <DataPageHeader t={t} />
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-default-500">
          <Icon icon="solar:database-outline" width={64} />
          <p>{t('No schemas match your search or filters.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <DataPageHeader t={t} />
      <EntityListView
        items={filteredSchemas}
        isLoading={false}
        searchId="data-search"
        searchPlaceholder={t('Search data models')}
        onSearch={setSearchQuery}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        emptyMessage={t('No schemas match your search or filters.')}
        cardKey={(schema: DatamodelSchema) => schema.id}
        renderCard={(schema: DatamodelSchema) => (
          <SchemaCard schema={schema} onOpenDetail={openMetrics} />
        )}
        cardGridClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        tableId="data-models-list-table"
        columnDefs={columnDefs}
        listRowMapper={listRowMapper}
      />
    </div>
  );
}

function DataPageHeader({ t }: { readonly t: (key: string) => string }) {
  return (
    <div className="flex flex-col gap-3 px-6 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h1 className="text-foreground font-sfui font-semibold text-3xl leading-[38px]">
            {t('Data Models')}
          </h1>
        </div>
      </div>
      <p className="font-poppins text-sm font-normal leading-5 text-default-500">
        {t('View data metrics and how its mapped')}
      </p>
    </div>
  );
}

function SchemaCard({
  schema,
  onOpenDetail,
}: {
  readonly schema: DatamodelSchema;
  readonly onOpenDetail: (schema: DatamodelSchema) => void;
}) {
  const { t } = useTranslation();

  const handleMetricsClick = () => {
    onOpenDetail(schema);
  };

  const description = getSchemaDescription(schema);

  return (
    <Card
      className="border border-default-200 shadow-sm rounded-xl overflow-hidden bg-content1"
      key={schema.id}
    >
      <CardHeader className="flex justify-between items-start gap-2 pb-0 pt-4 px-4">
        <span className="text-foreground font-poppins text-lg font-semibold leading-7 truncate flex-1 min-w-0">
          {formatKeyForDisplay(schema.name)}
        </span>
      </CardHeader>
      <CardBody
        className="pt-2 pb-3 px-4 cursor-pointer"
        role="button"
        tabIndex={0}
        aria-label={t('Open schema {{name}}', { name: schema.name })}
      >

        <p
          className="text-default-500 font-poppins text-sm font-normal leading-5 line-clamp-3 mb-3"
          title={description}
        >
          {description}
        </p>

        <div className="flex flex-wrap gap-2">
          <Chip size="sm" variant="flat" color="success" className="bg-success-100">
            Active
          </Chip>
        </div>

        {schema.metadata?.connector_reference && (
          <p className="text-default-500 font-poppins text-sm font-normal leading-5 mb-2">
            {schema.metadata.connector_reference}
          </p>
        )}
      </CardBody>
      <Divider />
      <CardFooter className="flex justify-end gap-2">
        <CtyButton
          className="w-full bg-primary text-primary-foreground font-medium"
          size="md"
          radius="sm"
          onPress={handleMetricsClick}
        >
          {t('Metrics')}
        </CtyButton>
      </CardFooter>
    </Card>
  );
}
